import * as types from '../mutation-types';
import { apolloClient } from '@/apollo';
import {
  CreateGroupDocument,
  DeleteGroupDocument,
  RestoreGroupDocument,
  UpdateGroupDocument,
  ProjectDocument,
  ProjectQueryVariables,
  ProjectQuery,
} from '@/graphql/operations.gen';
import updateProjectCache from '@utils/updateProjectCache';
import { map, reject } from 'ramda';

// initial state
const state = {};

// getters
const getters = {};

// actions
const actions = {
  /**
   * Create group.
   */
  create({ commit }, { projectId, type, name, label }) {
    commit(types.GROUP_CREATE, {
      meta: {
        analytics: [['event', 'group', 'create', label]],
      },
    });

    return apolloClient
      .mutate({
        mutation: CreateGroupDocument,
        variables: {
          projectId,
          name,
          type,
        },
        update(store, response) {
          const cacheData = store.readQuery<ProjectQuery, ProjectQueryVariables>({
            query: ProjectDocument,
            variables: { id: projectId },
          });

          const groupType = `${type}Groups`;

          store.writeQuery<ProjectQuery, ProjectQueryVariables>({
            query: ProjectDocument,
            data: {
              project: {
                ...cacheData.project,
                [groupType]: [...cacheData.project[groupType], response.data.createGroup],
              },
            },
            variables: {
              id: projectId,
            },
          });

          return response;
        },
      })
      .then((data) => data.data.createGroup);
  },

  /**
   * Delete group.
   */
  delete({ commit }, { id, type, projectId }: { id: string; type: 'source' | 'memo' | 'code'; projectId: string }) {
    return apolloClient
      .mutate({
        mutation: DeleteGroupDocument,
        variables: {
          id,
          type,
        },
        update(store, response) {
          const groupType: 'sourceGroups' | 'codeGroups' | 'memoGroups' = `${type}Groups`;

          updateProjectCache(store, projectId, (cachedProject) => ({
            [groupType]: reject((group) => group.id === id, cachedProject[groupType] as Array<{ id: string }>),
            // reference to deleted group must also be removed from all entities
            [`${type}s`]: map(
              (item) => ({
                ...item,
                groups: reject((group) => group.id === id, item.groups),
              }),
              cachedProject[`${type}s`] as Array<{ id: string; groups: Array<{ id: string }> }>
            ),
          }));

          return response;
        },
      })
      .then((response) => {
        commit(types.GROUP_DELETED, {
          groupType: response.data.deleteGroup.type,
        });

        return response.data.deleteGroup;
      });
  },

  /**
   * Restoure group.
   */
  restore({ commit }, { id, type, projectId }) {
    return apolloClient
      .mutate({
        mutation: RestoreGroupDocument,
        variables: {
          id,
        },
        update(store, response) {
          const groupType = `${type}Groups`;

          updateProjectCache(store, projectId, (cachedProject) => ({
            [groupType]: [...cachedProject[groupType], response.data.restoreGroup],
          }));

          return response;
        },
      })
      .then((response) => {
        commit(types.GROUP_RESTORED, {
          groupType: response.data.restoreGroup.type,
        });

        return response.data.restoreGroup;
      });
  },

  /**
   * Update group.
   */
  update({}, data) {
    return apolloClient.mutate({
      mutation: UpdateGroupDocument,
      variables: {
        ...data,
      },
    });
  },
};

// mutations
const mutations = {
  [types.GROUP_CREATE]() {},
  [types.GROUP_UPDATE]() {},
  [types.GROUP_DELETE]() {},
  [types.GROUP_DELETED]() {},
  [types.GROUP_RESTORED]() {},
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
